import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Car, Lock, Mail, CheckCircle2 } from "lucide-react";

export default function OwnerLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin/login", { email, password });
      
      if (data.user.role === "admin") {
        toast.error("This account is an Admin login — please use the mobile app instead.");
        setLoading(false);
        return;
      }
      
      if (data.user.role !== "owner") {
        toast.error("Invalid credentials or role.");
        setLoading(false);
        return;
      }

      localStorage.setItem("owner_token", data.token);
      localStorage.setItem("owner_name", data.user.name);
      localStorage.setItem("owner_provider_type", data.user.provider_type);
      toast.success("Welcome back!");
      nav("/provider/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-row min-h-screen">
      <div className="hidden md:flex w-[45%] min-h-screen bg-gradient-to-br from-[#0F2044] to-[#1A3C6E] relative px-10 py-12">
        <div className="w-full flex flex-col items-center justify-center text-center">
          <Car className="w-12 h-12 text-white" />
          <div className="font-heading text-4xl font-extrabold text-white tracking-tight mt-3">
            INSTAPARK
          </div>
          <p className="text-white/60 text-lg mt-4">
            Professional Valet Management Platform
          </p>
          <div className="mt-8 flex flex-col gap-3 text-left">
            {[
              "Real-time WebSocket tracking",
              "Multi-tenant provider management",
              "Guest QR retrieval flow",
            ].map((item) => (
              <div key={item} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-white/70 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute bottom-8 left-10 text-white/30 text-xs">
          © 2026 InstaPark
        </div>
      </div>

      <div className="w-full md:w-[55%] bg-[#F9FAFB] flex items-center justify-center min-h-screen px-4 py-10">
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-8 max-w-sm w-full fade-in-up relative">
          <h1 className="font-heading text-3xl font-bold text-[#0F2044] mb-1">Welcome back</h1>
          <p className="text-sm uppercase tracking-[0.18em] text-gray-500 font-medium mb-6">Owner Portal</p>

          <form onSubmit={submit} className="space-y-4" data-testid="owner-login-form">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Email <span className="text-red-500">*</span></label>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input data-testid="login-email-input" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} required
                  className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/20 outline-none" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Password <span className="text-red-500">*</span></label>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input data-testid="login-password-input" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required
                  className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 focus:border-[#1A3C6E] focus:ring-2 focus:ring-[#1A3C6E]/20 outline-none" />
              </div>
            </div>
            <button data-testid="login-submit-btn" disabled={loading}
              className="w-full btn-primary-navy rounded-xl py-3 font-semibold disabled:opacity-60">
              {loading ? "Signing in…" : "Sign In"}
            </button>
            <div className="text-center mt-2">
              <p className="text-xs text-gray-400 mb-2"><span className="text-red-500">*</span> Required fields</p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
